import { useCallback, useEffect, useRef, useState } from 'react';

import type { Coordinates } from '../utils/geolocation';
import { readCachedMapCenter, writeCachedMapCenter } from '../utils/mapCamera';

// How often the center the user is looking at is written to storage. The map
// reports its center continuously (panning, zooming, animated flyTos), so the
// hook throttles the writes to one every couple of seconds instead of persisting
// on every movement event.
export const MAP_CENTER_CACHE_INTERVAL_MS = 2000;

const isSameCenter = (first: Coordinates | null, second: Coordinates | null) =>
  first !== null &&
  second !== null &&
  first.latitude === second.latitude &&
  first.longitude === second.longitude;

/**
 * Remembers where the user was looking on the map.
 *
 * `initialCenter` is the center cached during a previous run (or `null` when
 * nothing was cached yet or the cache could not be read), so the map can open
 * where the user left off. `onCenterChange` is meant to be wired to the map's
 * movement events: the reported center is remembered and, every
 * `MAP_CENTER_CACHE_INTERVAL_MS`, persisted. The last center is flushed when
 * the hook unmounts so a move made right before leaving is not lost.
 */
export const useMapCenterCache = () => {
  const [initialCenter, setInitialCenter] = useState<Coordinates | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Center the map is currently showing, updated by every movement event.
  const visibleCenterRef = useRef<Coordinates | null>(null);
  // Center last written to storage, so an unchanged map is not re-persisted.
  const cachedCenterRef = useRef<Coordinates | null>(null);

  useEffect(() => {
    let isMounted = true;

    readCachedMapCenter()
      .then(center => {
        if (!isMounted) {
          return;
        }

        setInitialCenter(center);
        setIsLoading(false);
      })
      .catch(caughtError => {
        if (!isMounted) {
          return;
        }

        console.error('Failed to read the cached map center', caughtError);
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const persistVisibleCenter = () => {
      const visibleCenter = visibleCenterRef.current;

      if (
        !visibleCenter ||
        isSameCenter(visibleCenter, cachedCenterRef.current)
      ) {
        return;
      }

      cachedCenterRef.current = visibleCenter;
      writeCachedMapCenter(visibleCenter);
    };

    const interval = setInterval(
      persistVisibleCenter,
      MAP_CENTER_CACHE_INTERVAL_MS,
    );

    return () => {
      clearInterval(interval);
      // Persist the last seen center so reopening the app lands where the user
      // left off, even if that happened within the throttling interval.
      persistVisibleCenter();
    };
  }, []);

  const onCenterChange = useCallback((center: Coordinates) => {
    visibleCenterRef.current = center;
  }, []);

  return { initialCenter, isLoading, onCenterChange };
};
